import React from "react";
import { toast } from "sonner";
import { Route as RouteIcon, RefreshCw, Sparkles, ArrowRight, MapPin, User, Loader2, CheckCircle2 } from "lucide-react";
import { adminApi } from "../lib/api";

/**
 * RoutesPanel — admin tab for managing today's deliveries:
 *  1. View today's bookings grouped by driver (kanban-style)
 *  2. "Auto-tildel"-button → backend proposes best driver per unassigned booking, admin reviews + accepts
 *  3. "Optimér ruter"-button → runs OSRM TSP per driver
 */
export default function RoutesPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [proposals, setProposals] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [optimizeSummary, setOptimizeSummary] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get("/admin/routes/today");
      setData(data);
    } catch { toast.error("Kunne ikke hente ruter"); }
    finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const runAutoAssign = async () => {
    setBusy(true);
    setProposals(null);
    try {
      const { data } = await adminApi().post("/admin/routes/auto-assign", {});
      setProposals(data);
      if (data.unassigned_count === 0) toast.info("Ingen ikke-tildelte bookings i dag");
      else toast.success(`${data.unassigned_count} bookings analyseret`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-tildeling fejlede");
    } finally { setBusy(false); }
  };

  const acceptAllProposals = async () => {
    if (!proposals?.proposals?.length) return;
    const assignments = proposals.proposals
      .filter((p) => p.best_driver_id)
      .map((p) => ({ booking_id: p.booking_id, driver_id: p.best_driver_id }));
    if (!assignments.length) return toast.error("Ingen kandidater at acceptere");
    setBusy(true);
    try {
      const { data } = await adminApi().post("/admin/routes/accept", { assignments });
      toast.success(`${data.updated} bookings tildelt`);
      setProposals(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke gemme");
    } finally { setBusy(false); }
  };

  const runOptimizeAll = async () => {
    setBusy(true);
    setOptimizeSummary(null);
    try {
      const { data } = await adminApi().post("/admin/routes/optimize-all", {});
      setOptimizeSummary(data);
      const opt = (data.summary || []).filter((s) => s.optimized).length;
      toast.success(`${opt} chauffører optimeret`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Optimering fejlede");
    } finally { setBusy(false); }
  };

  if (loading && !data) return <div className="text-brand-text2" data-testid="routes-loading">Indlæser ruter...</div>;

  const drivers = data?.drivers || [];
  const grouped = data?.grouped || {};
  const totalToday = Object.values(grouped).reduce((sum, arr) => sum + arr.length, 0);
  const unassignedCount = (grouped["_unassigned"] || []).length;

  return (
    <div className="space-y-6" data-testid="routes-panel">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kicker">Algoritmisk · Real-time</div>
          <h3 className="font-display font-semibold text-2xl text-brand-dark mt-2">Ruter & tildeling</h3>
          <p className="text-sm text-brand-text2 mt-1">{data?.date} · {totalToday} bookings · {drivers.length} aktive chauffører · <span className={unassignedCount > 0 ? "text-amber-700 font-semibold" : ""}>{unassignedCount} ikke-tildelt</span></p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="btn-secondary inline-flex items-center gap-2" disabled={busy} data-testid="routes-refresh">
            <RefreshCw size={14} /> Genindlæs
          </button>
          <button
            onClick={runAutoAssign}
            className="btn-secondary inline-flex items-center gap-2"
            disabled={busy || unassignedCount === 0 || drivers.length === 0}
            data-testid="routes-auto-assign"
            title="Foreslå bedste chauffør for hver ikke-tildelt booking"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            Auto-tildel
          </button>
          <button
            onClick={runOptimizeAll}
            className="btn-primary inline-flex items-center gap-2"
            disabled={busy || totalToday < 2 || drivers.length === 0}
            data-testid="routes-optimize-all"
            title="Optimér rækkefølge af stops for alle chauffører via OSRM TSP"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <RouteIcon size={14} />}
            Optimér ruter
          </button>
        </div>
      </div>

      {/* Proposals review */}
      {proposals && proposals.proposals?.length > 0 && (
        <div className="bg-white border-2 border-brand-red p-5" data-testid="routes-proposals">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-display font-semibold text-brand-dark">Foreslåede tildelinger ({proposals.proposals.length})</div>
              <p className="text-xs text-brand-text2 mt-1">Algoritmen scorer hver chauffør på afstand + nuværende belastning</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setProposals(null)} className="btn-secondary text-xs" data-testid="routes-cancel-proposals">Annullér</button>
              <button onClick={acceptAllProposals} className="btn-primary text-xs inline-flex items-center gap-2" disabled={busy} data-testid="routes-accept-all">
                <CheckCircle2 size={14} /> Accepter alle
              </button>
            </div>
          </div>
          <div className="mt-4 space-y-2 max-h-96 overflow-y-auto">
            {proposals.proposals.map((p) => (
              <div key={p.booking_id} className="flex items-center justify-between gap-3 p-3 border border-brand bg-brand-bg2 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-xs text-brand-text2">{p.booking_number}</div>
                  <div className="text-brand-dark text-xs truncate">{p.pickup} → {p.delivery}</div>
                </div>
                <div className="text-xs">
                  {p.skipped ? <span className="text-red-700">Sprunget over: {p.skipped}</span> :
                    p.candidates?.length > 0 ? (
                      <div className="flex items-center gap-2">
                        <ArrowRight size={14} className="text-brand-red" />
                        <span className="font-semibold">{p.candidates[0].driver_name}</span>
                        <span className="text-brand-text2">({p.candidates[0].distance_km} km, score {p.candidates[0].score})</span>
                      </div>
                    ) : <span className="text-amber-700">Ingen ledig kapacitet</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Optimize result */}
      {optimizeSummary && (
        <div className="bg-emerald-50 border border-emerald-300 p-4" data-testid="routes-optimize-summary">
          <div className="font-semibold text-emerald-900">Optimeringsresultat</div>
          <div className="mt-2 space-y-1 text-sm text-emerald-900">
            {optimizeSummary.summary.map((s) => (
              <div key={s.driver_id} className="flex items-center gap-3">
                <User size={14} /> <strong>{s.driver_name}</strong>:
                {s.optimized
                  ? <span> {s.stops} stops · {s.distance_km} km · {s.duration_min} min</span>
                  : <span className="text-emerald-700/70"> {s.reason}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Kanban-style columns */}
      {drivers.length === 0 ? (
        <div className="bg-white border border-brand p-10 text-center" data-testid="routes-no-drivers">
          <User size={32} className="mx-auto text-brand-text2" />
          <div className="font-display font-semibold text-brand-dark mt-3">Ingen aktive chauffører</div>
          <p className="text-sm text-brand-text2 mt-1">Opret en chauffør under "Chauffør-konti"-tab for at komme i gang.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="routes-kanban">
          {/* Unassigned column */}
          {unassignedCount > 0 && (
            <div className="bg-amber-50 border-2 border-amber-300 p-4">
              <div className="flex items-center gap-2">
                <RouteIcon size={16} className="text-amber-700" />
                <div className="font-display font-semibold text-amber-900">Ikke tildelt</div>
                <span className="ml-auto text-xs bg-amber-200 px-2 py-0.5 font-bold">{unassignedCount}</span>
              </div>
              <div className="mt-3 space-y-2">
                {(grouped["_unassigned"] || []).map((b) => (
                  <BookingCard key={b.id} b={b} />
                ))}
              </div>
            </div>
          )}
          {/* One column per driver */}
          {drivers.map((d) => {
            const stops = grouped[d.id] || [];
            return (
              <div key={d.id} className="bg-white border border-brand p-4">
                <div className="flex items-center gap-2">
                  <User size={16} className="text-brand-red" />
                  <div className="font-display font-semibold text-brand-dark">{d.name}</div>
                  <span className="ml-auto text-xs bg-slate-100 px-2 py-0.5 font-bold">{stops.length}</span>
                </div>
                <div className="text-xs text-brand-text2 truncate">{d.email}</div>
                <div className="mt-3 space-y-2">
                  {stops.length === 0
                    ? <div className="text-xs text-brand-text2 italic">Ingen stops i dag</div>
                    : stops.map((b) => <BookingCard key={b.id} b={b} showOrder />)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BookingCard({ b, showOrder }) {
  const statusColor = b.booking_status === "delivered" ? "bg-emerald-100 text-emerald-900"
    : b.booking_status === "in_transit" ? "bg-blue-100 text-blue-900"
    : "bg-slate-100 text-slate-900";
  return (
    <div className="bg-brand-bg2 border border-brand p-2.5 text-xs">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-brand-text2">{b.booking_number}</span>
        <span className={`px-1.5 py-0.5 ${statusColor} text-[10px] uppercase tracking-wider font-bold`}>
          {b.booking_status}
        </span>
      </div>
      <div className="mt-1 flex items-start gap-1.5">
        {showOrder && b.route_order && (
          <span className="bg-brand-red text-white w-5 h-5 inline-flex items-center justify-center font-bold flex-shrink-0">{b.route_order}</span>
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-1 text-brand-dark"><MapPin size={10} className="text-brand-red" /> {b.pickup_city}</div>
          <div className="flex items-center gap-1 text-brand-dark"><ArrowRight size={10} className="text-brand-red" /> {b.delivery_city}</div>
        </div>
      </div>
      <div className="mt-1 text-brand-text2">{b.delivery_type} · {b.total_amount_dkk} kr</div>
    </div>
  );
}
